import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, TemplateRef, ChangeDetectorRef, HostListener, NgZone, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, NavigationStart, ActivatedRoute } from '@angular/router';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent } from '../primeng';
import { ActionDropdownTriggerDirective } from '../../directives/action-dropdown-trigger.directive';
import { RequestCreditsComponent } from '../request-credits/request-credits.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { SandboxApiService, AuthApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../environments/environment';
import { filter, take } from 'rxjs/operators';
import { CanComponentDeactivate } from '../../../guards/my-projects-deactivate.guard';
import { Observable } from 'rxjs';

export interface ProjectData {
  id: string;
  notebookName: string;
  storageSize: string;
  type: string;
  pvcName: string;
  cpuRequest: string;
  cpuLimit: string;
  memoryRequest: string;
  memoryLimit: string;
  status: string;
  createdAt: string;
}

@Component({
  selector: 'app-my-project',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableComponent,
    ModalComponent,
    RequestCreditsComponent,
    ConfirmationDialogComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './my-project.component.html',
  styleUrl: './my-project.component.scss',
})
export class MyProjectComponent implements OnInit, AfterViewInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;
  @ViewChild('typeCell', { static: false }) typeCellTemplate!: TemplateRef<any>;
  @ViewChild('dateCell', { static: false }) dateCellTemplate!: TemplateRef<any>;
  @ViewChild('expandCell', { static: false }) expandCellTemplate!: TemplateRef<any>;
  @ViewChild('childRow', { static: false }) childRowTemplate!: TemplateRef<any>;
  @ViewChild(RequestCreditsComponent, { static: false }) requestCreditsComponent!: RequestCreditsComponent;

  columns: TableColumn[] = [
    { 
      key: '_expand', 
      label: '',
      sortable: false,
      align: 'left',
      fixed: 'left'
    },
    { 
      key: 'notebookName', 
      label: 'Notebook Name',
      sortable: false,
      width: '25%'
    },
    { 
      key: 'type', 
      label: 'Type',
      sortable: false,
      width: '10%'
    },
    { 
      key: 'storageSize', 
      label: 'Storage Size',
      sortable: false,
      width: '13%'
    },
    { 
      key: 'pvcName', 
      label: 'Pvc Name',
      sortable: false,
      width: '12%'
    },
    { 
      key: 'status', 
      label: 'Status',
      sortable: false,
      width: '10%'
    },
    { 
      key: 'createdAt', 
      label: 'Created At',
      sortable: false,
      width: '12%'
    },
    { 
      key: 'action', 
      label: 'Action',
      sortable: false,
      width: '10%',
      align: 'center',
      fixed: 'right'
    }
  ];

  projectsData: ProjectData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: false
  };

  loading = false;
  emptyMessage = 'No Notebooks Available';
  creditsLoading = true; // Loading state for credits section
  creditsRemaining: number = 0; // Will be populated from API
  creditsExpired: boolean = false; // Will be populated from API
  creditsExpirationDate: string | null = null; // Will be populated from API
  /** True when user already has a pending credit request (from GET user/credit/request) */
  hasPendingCreditRequest = false;
  pendingCreditRequestLoading = false;
  /** Pending request's additionalInfo for prepopulating the Request Credits form */
  pendingCreditRequestData: any = null;
  /** Pending credit request ID (for withdraw API) */
  pendingCreditRequestId: string | null = null;
  showWithdrawCreditModal = false;
  withdrawCreditLoading = false;
  /** Request credits history modal (granted / rejected / pending) */
  showRequestCreditsHistoryModal = false;
  creditRequestHistory: { requestedOn: string; status: string; id?: string; originalData?: any }[] = [];
  creditRequestHistoryLoading = false;
  openDropdownId: string | null = null;
  /** Track which history action dropdown is open (index in creditRequestHistory array) */
  openHistoryActionIndex: number | null = null;
  private isLoadingNotebooks = false; // Flag to prevent multiple simultaneous API calls
  private notebooksLoaded = false; // Flag to prevent reloading on re-initialization
  
  // Polling intervals for notebooks with "Opening" status
  private statusPollingIntervals: Map<string, any> = new Map();
  
  // Track expanded rows for child row functionality
  expandedRows: Set<string> = new Set();
  
  // Tab state
  activeTab: 'create' | 'list' | 'request-credits' = 'list'; // Default to 'list' (Notebook List)
  
  // Modal state (removed - now using tabs)
  showCreateNotebookModal = false;
  
  // Confirmation modal state for notebook ready
  showNotebookReadyModal = false;
  readyNotebook: ProjectData | null = null;
  readyNotebookUrl: string | null = null;
  showPolicyModal = false;
  notebookForm!: FormGroup;
  
  // Navigation guard modal state
  showNavigationGuardModal = false;
  pendingNavigation: any = null;
  openingNotebooks: ProjectData[] = [];
  
  // Compute role access
  hasComputeRole: boolean = false;
  /** True only when the active roles array contains Compute (not pending). Used to hide GPU warning. */
  hasActiveComputeRole: boolean = false;
  pendingRoles: string[] = [];
  /** Bound handler for userRolesUpdated event (so we can remove it in ngOnDestroy) */
  private userRolesUpdatedHandler = () => this.onUserRolesUpdated();
  
  instanceTypeOptions = [
    { label: 'CPU', value: 'CPU' },
    { label: 'GPU', value: 'GPU' }
  ];

  /** User category options rendered as cards (populated from GPU instance types API) */
  userCategoryOptions: {
    label: string;
    value: string;
    memory: string;
    description: string;
    description1: string;
    session: string;
  }[] = [];

  /** Cached GPU instance types fetched from API when GPU is selected */
  gpuInstanceTypes: any[] = [];

  private sandboxApiService = inject(SandboxApiService);
  private authApiService = inject(AuthApiService);
  private toasterService = inject(ToasterService);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private keycloak = inject(KeycloakService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  constructor(
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private ngZone: NgZone
  ) {}

  async ngOnInit(): Promise<void> {
    // Initialize form
    this.initNotebookForm();
    
    // Restore tab state from URL query params (so refresh keeps the same tab)
    const tabFromUrl = this.route.snapshot.queryParamMap.get('tab');
    if (tabFromUrl && (tabFromUrl === 'list' || tabFromUrl === 'create' || tabFromUrl === 'request-credits')) {
      this.activeTab = tabFromUrl;
    }
    
    // Wait for Keycloak to be ready before making API calls
    await this.waitForKeycloakReady();
    
    // Check if user has Compute role
    await this.checkComputeRole();
    
    // Listen for role updates (e.g. after Upgrade Your Role modal submits Compute request)
    if (typeof window !== 'undefined') {
      window.addEventListener('userRolesUpdated', this.userRolesUpdatedHandler);
    }
    
    // Load credit balance first (shows skeleton while loading)
    await this.loadCreditBalance();
    
    // If user has active compute role, check for pending credit request (form only shown when active)
    if (this.hasActiveComputeRole) {
      await this.checkPendingCreditRequest();
    }
    
    // Then load notebooks on page load
    if (!this.notebooksLoaded) {
      await this.loadNotebooks();
    }
  }

  /**
   * Wait for Keycloak to be initialized and have a token available
   */
  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      
      // If Keycloak is already initialized and has a token, proceed immediately
      if (keycloakInstance?.authenticated && keycloakInstance?.token) {
        console.log('Keycloak is ready, token available');
        return;
      }
      
      // Wait for Keycloak to be initialized (max 5 seconds)
      const maxWaitTime = 5000; // 5 seconds
      const checkInterval = 100; // Check every 100ms
      const startTime = Date.now();
      
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) {
          console.log('Keycloak is ready after waiting');
          return;
        }
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      }
      
      console.warn('Keycloak ready check timed out, proceeding anyway');
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
      // Proceed anyway - the API calls will handle 401 errors
    }
  }

  async loadNotebooks(skipLoadingState: boolean = false): Promise<void> {
    // Prevent multiple simultaneous calls
    if (this.isLoadingNotebooks) {
      console.log('Notebooks are already being loaded, skipping...');
      return;
    }

    try {
      this.isLoadingNotebooks = true;
      if (!skipLoadingState) {
        this.loading = true;
      }
      const response = await this.sandboxApiService.listNotebooks();
      console.log('Notebooks API response:', response);
      
      // Map API response to projectsData
      // Adjust the mapping based on the actual API response structure
      let notebooksArray: any[] = [];
      
      // Handle different response structures
      if (Array.isArray(response)) {
        notebooksArray = response;
      } else if (response?.result) {
        // Check for result array (most common structure: { result: [...], paginationInfo: {...} })
        if (Array.isArray(response.result)) {
          notebooksArray = response.result;
        } else if (response.result.results && Array.isArray(response.result.results)) {
          notebooksArray = response.result.results;
        } else if (response.result.data && Array.isArray(response.result.data)) {
          notebooksArray = response.result.data;
        }
      } else if (response?.notebooks && Array.isArray(response.notebooks)) {
        notebooksArray = response.notebooks;
      } else if (response?.data && Array.isArray(response.data)) {
        notebooksArray = response.data;
      } else if (response?.items && Array.isArray(response.items)) {
        notebooksArray = response.items;
      }
      
      // Log warning if no items found
      if (notebooksArray.length === 0 && response) {
        console.warn('No notebooks array found in response. Response structure:', Object.keys(response));
        console.warn('Full response:', response);
      }
      
      // Map notebooks to projectsData: Type = GPU if instanceType key present, else CPU
      this.projectsData = notebooksArray.map((notebook: any, index: number) => {
        const storageSize = notebook.storageSize || notebook.storage || '';
        const type = notebook.instanceType != null && notebook.instanceType !== '' ? 'GPU' : 'CPU';
        return {
        id: notebook.id || notebook.notebookId || notebook.name || `notebook-${index}`,
        notebookName: notebook.name || notebook.notebookName || '',
        storageSize,
        type,
        pvcName: notebook.pvcName || notebook.pvc || '',
        cpuRequest: notebook.cpuRequest || notebook.cpu || '',
        cpuLimit: notebook.cpuLimit || notebook.cpuLimit || '',
        memoryRequest: notebook.memoryRequest || notebook.memory || '',
        memoryLimit: notebook.memoryLimit || notebook.memoryLimit || '',
        status: notebook.status || 'Unknown',
        createdAt: notebook.createdAt || notebook.created_at || notebook.created || ''
      };
      });
      
      console.log('Mapped projectsData:', this.projectsData);
      console.log('Number of notebooks:', this.projectsData.length);
      
      // Update pagination
      this.pagination.totalItems = this.projectsData.length;
      this.notebooksLoaded = true;
      this.isLoadingNotebooks = false;
      
      // Update loading state and ensure change detection runs
      this.loading = false;
      
      // Mark for check and detect changes immediately
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      // Also trigger change detection in next microtask to ensure view updates
      // This handles cases where change detection might be skipped
      Promise.resolve().then(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
      
      // Start polling for notebooks with "Opening" status
      this.startPollingForOpeningNotebooks();
    } catch (error) {
      console.error('Error loading notebooks:', error);
      this.loading = false;
      this.isLoadingNotebooks = false;
      // Keep existing data or show empty state
      if (this.projectsData.length === 0) {
        this.projectsData = [];
        this.pagination.totalItems = 0;
      }
      // Ensure change detection runs even on error
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      Promise.resolve().then(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
    }
  }

  initNotebookForm(): void {
    this.notebookForm = this.fb.group({
      name: ['', [Validators.required, this.notebookNameValidator]],
      instanceType: ['CPU', [Validators.required]],
      userCategory: [''],
      policyAgreed: [false, [Validators.requiredTrue]]
    });
  }

  // Custom validator for notebook name
  notebookNameValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null; // Let required validator handle empty values
    }

    // Only lowercase letters are accepted
    if (value !== value.toLowerCase()) {
      return { lowercaseOnly: true };
    }

    // Must start with a letter or number
    if (!/^[a-z0-9]/.test(value)) {
      return { mustStartWithLetterOrNumber: true };
    }

    // Hyphen allowed inside segments (but not at start or end)
    // Valid: abc-def, abc123-def, a-b-c
    // Invalid: -abc, abc-, --abc
    if (/^-|-$|--/.test(value)) {
      return { invalidHyphen: true };
    }

    // Only allow lowercase letters, numbers, and hyphens
    if (!/^[a-z0-9-]+$/.test(value)) {
      return { invalidCharacters: true };
    }

    return null;
  };

  getNotebookNameErrors(): string {
    const nameControl = this.notebookForm.get('name');
    if (nameControl?.errors) {
      if (nameControl.errors['required']) {
        return 'Notebook name is required';
      }
      if (nameControl.errors['lowercaseOnly']) {
        return 'Only lowercase letters are accepted';
      }
      if (nameControl.errors['mustStartWithLetterOrNumber']) {
        return 'Must start with a letter or number';
      }
      if (nameControl.errors['invalidHyphen']) {
        return 'Hyphen allowed inside segments only';
      }
      if (nameControl.errors['invalidCharacters']) {
        return 'Only lowercase letters, numbers, and hyphens are allowed';
      }
    }
    return '';
  }

  // Check individual validation rules
  isLowercaseOnlyValid(): boolean {
    const value = this.notebookForm.get('name')?.value;
    if (!value) return false;
    return value === value.toLowerCase();
  }

  isStartsWithLetterOrNumberValid(): boolean {
    const value = this.notebookForm.get('name')?.value;
    if (!value) return false;
    return /^[a-z0-9]/.test(value);
  }

  isHyphenValid(): boolean {
    const value = this.notebookForm.get('name')?.value;
    if (!value) return false;
    // Hyphen is valid if it's not at start, not at end, and not consecutive
    return !/^-|-$|--/.test(value);
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    if (this.activeTab === 'list') {
      this.assignCellTemplates();
      this.cdr.detectChanges();
    }
  }
  
  /**
   * Derive notebook type from storage size: 50Gi → GPU, 10Gi → CPU
   */
  getNotebookTypeFromStorage(storageSize: string): string {
    if (!storageSize || typeof storageSize !== 'string') return '—';
    const normalized = storageSize.trim().replace(/\s+/g, '').toLowerCase();
    if (normalized === '65gi') return 'GPU';
    if (normalized === '10gi') return 'CPU';
    return '—';
  }

  /**
   * Format date to DD-MMM-YYYY format
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      
      const day = date.getDate().toString().padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }
  
  /**
   * Get type chip class for Type column (CPU / GPU)
   */
  getTypeChipClass(type: string): string {
    if (!type) return 'type-chip type-chip-default';
    const t = type.trim().toUpperCase();
    if (t === 'CPU') return 'type-chip type-chip-cpu';
    if (t === 'GPU') return 'type-chip type-chip-gpu';
    return 'type-chip type-chip-default';
  }

  /**
   * Get status chip class based on status value
   */
  getStatusClass(status: string): string {
    const normalizedStatus = (status || '').toLowerCase();
    if (normalizedStatus === 'running') {
      return 'status-chip status-chip--running';
    } else if (normalizedStatus === 'stopped') {
      return 'status-chip status-chip--stopped';
    } else if (normalizedStatus === 'opening') {
      return 'status-chip status-chip--opening';
    }
    return 'status-chip status-chip--default';
  }
  
  /**
   * Check if status is "Opening"
   */
  isOpeningStatus(status: string): boolean {
    if (!status) return false;
    return status.trim().toLowerCase() === 'opening';
  }

  /**
   * Check if status is "Running" (case-insensitive)
   */
  isRunningStatus(status: string): boolean {
    if (!status) return false;
    return status.trim().toLowerCase() === 'running';
  }

  /**
   * Check if status is "Stopped" (case-insensitive)
   */
  isStoppedStatus(status: string): boolean {
    if (!status) return false;
    return status.trim().toLowerCase() === 'stopped';
  }
  
  /**
   * Start polling for notebooks with "Opening" status
   */
  startPollingForOpeningNotebooks(): void {
    // Clear existing intervals first
    this.stopAllPolling();
    
    // Start polling for each notebook with "Opening" status
    this.projectsData.forEach((notebook) => {
      if (this.isOpeningStatus(notebook.status) && notebook.notebookName) {
        this.startStatusPolling(notebook);
      }
    });
  }
  
  /**
   * Start polling status for a specific notebook
   */
  startStatusPolling(notebook: ProjectData): void {
    // Use notebook name instead of ID for the status check API
    const notebookName = notebook.notebookName;
    const notebookId = notebook.id || notebook.notebookName; // Keep ID for tracking intervals
    
    // Don't start polling if already polling for this notebook or if name is missing
    if (!notebookName || this.statusPollingIntervals.has(notebookId)) {
      return;
    }
    
    console.log(`Starting status polling for notebook: ${notebookName}`);
    
    const interval = setInterval(async () => {
      try {
        // Use notebook name for the API call (pass base URL by notebook type for CPU/GPU)
        const baseUrl = this.getSandboxBaseUrlForNotebook(notebook.type);
        const response = await this.sandboxApiService.notebookStatusCheck(notebookName, baseUrl);
        console.log(`Status check response for ${notebookName}:`, response);
        
        // Extract status from response (adjust based on actual API response structure)
        let newStatus = notebook.status;
        let notebookUrl: string | null = null;
        
        if (response?.status) {
          newStatus = response.status;
        } else if (response?.data?.status) {
          newStatus = response.data.status;
        } else if (response?.result?.status) {
          newStatus = response.result.status;
        }
        
        // Extract notebook URL from response if available
        if (response?.url) {
          notebookUrl = response.url;
        } else if (response?.data?.url) {
          notebookUrl = response.data.url;
        } else if (response?.result?.url) {
          notebookUrl = response.result.url;
        } else if (response?.notebookUrl) {
          notebookUrl = response.notebookUrl;
        } else if (response?.data?.notebookUrl) {
          notebookUrl = response.data.notebookUrl;
        } else if (response?.result?.notebookUrl) {
          notebookUrl = response.result.notebookUrl;
        }
        
        // Update the notebook status in projectsData
        const notebookIndex = this.projectsData.findIndex(
          (n) => n.notebookName === notebookName
        );
        
        if (notebookIndex !== -1) {
          const normalizedNewStatus = (newStatus || '').toLowerCase();
          const normalizedOldStatus = (notebook.status || '').toLowerCase();
          
          // Update status if it changed
          if (normalizedNewStatus !== normalizedOldStatus) {
            this.projectsData[notebookIndex].status = newStatus;
            this.cdr.detectChanges();
            console.log(`Status updated for ${notebookName}: ${normalizedOldStatus} -> ${normalizedNewStatus}`);
            
            // Show confirmation modal when status changes from "Opening" to "Running"
            if (normalizedOldStatus === 'opening' && normalizedNewStatus === 'running') {
              this.showNotebookReadyConfirmation(notebook, notebookUrl);
            }
          }
          
          // Stop polling if status is no longer "Opening"
          if (normalizedNewStatus !== 'opening') {
            this.stopStatusPolling(notebookId);
            console.log(`Stopped polling for ${notebookName} - status is now ${normalizedNewStatus}`);
          }
        }
      } catch (error) {
        console.error(`Error checking status for notebook ${notebookName}:`, error);
        // Stop polling when API response is not 200 (e.g. 404, 5xx)
        this.stopStatusPolling(notebookId);
      }
    }, 10000); // Poll every 10 seconds
    
    this.statusPollingIntervals.set(notebookId, interval);
  }
  
  /**
   * Stop polling status for a specific notebook
   */
  stopStatusPolling(notebookId: string): void {
    const interval = this.statusPollingIntervals.get(notebookId);
    if (interval) {
      clearInterval(interval);
      this.statusPollingIntervals.delete(notebookId);
      console.log(`Stopped status polling for notebook: ${notebookId}`);
    }
  }
  
  /**
   * Stop all polling intervals
   */
  stopAllPolling(): void {
    this.statusPollingIntervals.forEach((interval, notebookId) => {
      clearInterval(interval);
      console.log(`Cleared polling interval for notebook: ${notebookId}`);
    });
    this.statusPollingIntervals.clear();
  }
  
  /**
   * Called when header dispatches userRolesUpdated after a successful role request (e.g. Compute).
   * Refreshes compute role state so "Compute Role Pending" message updates.
   */
  private onUserRolesUpdated(): void {
    this.checkComputeRole();
  }

  /**
   * Cleanup on component destroy
   */
  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('userRolesUpdated', this.userRolesUpdatedHandler);
    }
    this.stopAllPolling();
  }

  onPageChange(event: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: event
    };
  }

  onPageSizeChange(event: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: event,
      currentPage: 1
    };
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    // Handle sorting logic here
    console.log('Sort changed:', event);
  }

  toggleActionDropdown(event: Event, row: ProjectData): void {
    event.stopPropagation();
    // Use notebookName as unique identifier since it's guaranteed to be present
    const rowId = row.notebookName || row.id || '';
    const wasOpen = this.openDropdownId === rowId;
    this.openDropdownId = wasOpen ? null : rowId;
    // CD run by appActionDropdownTrigger directive
    // Position dropdown using fixed positioning to escape scroll container
    if (!wasOpen && this.openDropdownId === rowId) {
      // Use a slightly longer timeout to ensure Angular has rendered the element
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        if (!button) {
          console.error('Action button not found');
          return;
        }
        
        // Try to find dropdown by searching from the button's parent wrapper
        const wrapper = button.closest('.action-cell-wrapper');
        let dropdown = wrapper?.querySelector(`[data-dropdown-id="${rowId}"]`) as HTMLElement;
        
        // Fallback: search from document
        if (!dropdown) {
          dropdown = document.querySelector(`[data-dropdown-id="${rowId}"]`) as HTMLElement;
        }
        
        if (button && dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.display = 'block';
          dropdown.style.visibility = 'visible';
          dropdown.style.opacity = '1';
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
          dropdown.style.width = 'auto';
          dropdown.style.minWidth = 'max-content';
          console.log('Dropdown positioned:', { top: dropdown.style.top, left: dropdown.style.left, visible: dropdown.style.visibility });
        } else {
          console.error('Dropdown not found:', { button: !!button, dropdown: !!dropdown, rowId, wrapper: !!wrapper });
        }
      }, 50);
    }
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
  }

  isDropdownOpen(row: ProjectData): boolean {
    // Use notebookName as unique identifier since it's guaranteed to be present
    const rowId = row.notebookName || row.id || '';
    return this.openDropdownId === rowId;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close dropdown when clicking outside
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
    // Close history action dropdown when clicking outside
    if (this.openHistoryActionIndex !== null && !target.closest('.action-cell')) {
      this.openHistoryActionIndex = null;
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    // Close dropdown when scrolling
    if (this.openDropdownId !== null) {
      this.closeActionDropdown();
    }
  }


  /**
   * Toggle row expansion for child rows
   */
  toggleRowExpansion(row: ProjectData): void {
    const rowId = row.notebookName || row.id || '';
    if (this.expandedRows.has(rowId)) {
      this.expandedRows.delete(rowId);
    } else {
      this.expandedRows.add(rowId);
    }
    this.cdr.detectChanges();
  }

  /**
   * Check if a row is expanded
   */
  isRowExpanded(row: ProjectData): boolean {
    const rowId = row.notebookName || row.id || '';
    return this.expandedRows.has(rowId);
  }

  /**
   * Get the bound isRowExpanded function for passing to child component
   */
  getIsRowExpanded(): (row: any) => boolean {
    return (row: any) => this.isRowExpanded(row);
  }

  async handleAction(action: string, row: ProjectData): Promise<void> {
    this.closeActionDropdown();
    
    switch (action) {
      case 'open':
        await this.openNotebook(row);
        break;
      case 'stop':
        await this.stopNotebook(row);
        break;
      case 'start':
        await this.startNotebook(row);
        break;
      case 'delete':
        await this.deleteNotebook(row);
        break;
      default:
        console.log('Unknown action:', action, row);
    }
  }

  /**
   * Stop a notebook
   */
  async stopNotebook(row: ProjectData): Promise<void> {
    try {
      // Prepare payload with notebook name
      const payload = {
        name: row.notebookName
      };
      
      console.log('Stopping notebook with payload:', payload);
      const baseUrl = this.getSandboxBaseUrlForNotebook(row.type);
      const response = await this.sandboxApiService.stopNotebook(payload, baseUrl);
      console.log('Notebook stopped successfully:', response);
      this.googleAnalytics.trackMyProjectNotebookStop(row.notebookName);

      // Refresh the notebook list after successful stop
      this.notebooksLoaded = false;
      await this.loadNotebooks();
    } catch (error) {
      console.error('Error stopping notebook:', error);
      this.toasterService.error('Failed to stop notebook. Please try again.');
    }
  }

  /**
   * Start a notebook
   */
  async startNotebook(row: ProjectData): Promise<void> {
    try {
      // Prepare payload with notebook name
      const payload = {
        name: row.notebookName
      };
      
      console.log('Starting notebook with payload:', payload);
      const baseUrl = this.getSandboxBaseUrlForNotebook(row.type);
      const response = await this.sandboxApiService.startNotebook(payload, baseUrl);
      console.log('Notebook started successfully:', response);
      this.googleAnalytics.trackMyProjectNotebookStart(row.notebookName);

      // Update the status locally to "Opening" immediately for better UX
      const notebookIndex = this.projectsData.findIndex(
        (n) => n.notebookName === row.notebookName
      );
      if (notebookIndex !== -1) {
        this.projectsData[notebookIndex].status = 'Opening';
        this.cdr.detectChanges();
        
        // Start polling for this notebook to track status changes
        this.startStatusPolling(this.projectsData[notebookIndex]);
      }
      
      // Optionally refresh the notebook list after a delay to get updated status
      // But don't show loading skeleton - let the status update naturally
      setTimeout(async () => {
        this.notebooksLoaded = false;
        await this.loadNotebooks();
      }, 2000); // Refresh after 2 seconds to get updated status
    } catch (error) {
      console.error('Error starting notebook:', error);
      this.toasterService.error('Failed to start notebook. Please try again.');
    }
  }

  /**
   * Delete a notebook
   */
  async deleteNotebook(row: ProjectData): Promise<void> {
    try {
      // Prepare payload with notebook name
      const payload = {
        name: row.notebookName
      };
      
      console.log('Deleting notebook with payload:', payload);
        const baseUrl = this.getSandboxBaseUrlForNotebook(row.type);
        const response = await this.sandboxApiService.deleteNotebook(payload, baseUrl);
        console.log('Notebook deleted successfully:', response);
        this.googleAnalytics.trackMyProjectNotebookDelete(row.notebookName);

        // Remove the notebook from the local array immediately for better UX
      this.projectsData = this.projectsData.filter(
        (n) => n.notebookName !== row.notebookName
      );
      this.pagination.totalItems = this.projectsData.length;
      this.cdr.detectChanges();
      
      // Show success toaster message
      this.toasterService.success("You've successfully deleted the Notebook");
      
      // Optionally refresh the notebook list to ensure consistency
      setTimeout(async () => {
        this.notebooksLoaded = false;
        await this.loadNotebooks();
      }, 500);
    } catch (error) {
      console.error('Error deleting notebook:', error);
      this.toasterService.error('Failed to delete notebook. Please try again.');
      // Refresh list on error to ensure consistency
      this.notebooksLoaded = false;
      await this.loadNotebooks();
    }
  }

  createNotebook(): void {
    // Switch to create notebook tab
    this.activeTab = 'create';
    this.initNotebookForm(); // Reset form when switching to create tab
  }

  async setActiveTab(tabKey: 'create' | 'list' | 'request-credits'): Promise<void> {
    // Prevent switching to request-credits if user doesn't have compute role (neither active nor pending)
    if (tabKey === 'request-credits' && !this.hasComputeRole) {
      console.log('Cannot access Request Credits tab: Compute role required');
      // Still allow switching to show the message
      this.activeTab = tabKey;
      this.updateTabQueryParam(tabKey);
      return;
    }
    
    this.activeTab = tabKey;
    this.updateTabQueryParam(tabKey);
    if (tabKey === 'create') {
      this.initNotebookForm(); // Reset form when switching to create tab
    }
    
    // Assign templates when switching to list tab (templates are conditionally rendered)
    if (tabKey === 'list') {
      setTimeout(() => {
        this.assignCellTemplates();
      }, 100);
    }
    // When switching to request-credits, refresh pending request check (only when user has active Compute)
    if (tabKey === 'request-credits' && this.hasActiveComputeRole) {
      this.checkPendingCreditRequest();
    }
  }

  /** Update URL query param so the active tab persists on refresh */
  private updateTabQueryParam(tabKey: 'create' | 'list' | 'request-credits'): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabKey },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * Assign cell templates to columns
   */
  private assignCellTemplates(): void {
    // Assign expand cell template
    if (this.expandCellTemplate) {
      const expandColumn = this.columns.find(col => col.key === '_expand');
      if (expandColumn) {
        expandColumn.cellTemplate = this.expandCellTemplate;
        console.log('Expand cell template assigned');
      } else {
        console.warn('Expand column not found');
      }
    } else {
      console.warn('Expand cell template not found');
    }
    
    if (this.actionCellTemplate) {
      const actionColumn = this.columns.find(col => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.actionCellTemplate;
      }
    }
    
    // Assign type cell template
    if (this.typeCellTemplate) {
      const typeColumn = this.columns.find(col => col.key === 'type');
      if (typeColumn) {
        typeColumn.cellTemplate = this.typeCellTemplate;
      }
    }
    
    // Assign status cell template
    if (this.statusCellTemplate) {
      const statusColumn = this.columns.find(col => col.key === 'status');
      if (statusColumn) {
        statusColumn.cellTemplate = this.statusCellTemplate;
      }
    }
    
    // Assign date cell template
    if (this.dateCellTemplate) {
      const dateColumn = this.columns.find(col => col.key === 'createdAt');
      if (dateColumn) {
        dateColumn.cellTemplate = this.dateCellTemplate;
      }
    }
    
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  /**
   * Load credit balance from API
   */
  async loadCreditBalance(): Promise<void> {
    this.creditsLoading = true;
    try {
      const response = await this.authApiService.getCreditBalance();
      console.log('Credit balance API response:', response);
      
      // Support both known payload shapes:
      // 1) { result: { balance: number, expiration_date: string, isValid: boolean } }
      // 2) { result: { balance: { balance: number, expiration_date: string, isValid: boolean } } }
      const result = response?.result ?? {};
      const balanceData =
        result?.balance && typeof result.balance === 'object'
          ? result.balance
          : result;

      if (balanceData && typeof balanceData === 'object') {
        const rawBalance = balanceData.balance;
        if (rawBalance !== undefined && rawBalance !== null && rawBalance !== '') {
          const parsedBalance = Number(rawBalance);
          this.creditsRemaining = Number.isFinite(parsedBalance) ? parsedBalance : 0;
        } else {
          this.creditsRemaining = 0;
        }

        this.creditsExpirationDate =
          balanceData.expiration_date ?? balanceData.expirationDate ?? null;

        // If API explicitly tells validity, prefer that; otherwise derive from date.
        if (typeof balanceData.isValid === 'boolean') {
          this.creditsExpired = !balanceData.isValid;
        } else {
          this.creditsExpired = this.isCreditsExpired();
        }
      }
      this.creditsLoading = false;
    } catch (error: any) {
      console.error('Error loading credit balance:', error);
      this.creditsLoading = false;
      
      // Check if it's a 403 error with the specific message about GPU compute role
      const errorStatus = error?.status || error?.error?.status || error?.error?.error?.status;
      const errorDetail = error?.error?.detail || error?.detail || error?.error?.error?.detail || '';
      const errorType = error?.error?.type || error?.type || error?.error?.error?.type || '';
      
      if (errorStatus === 403 || errorType === 'dx:controlPlane:forbidden') {
        if (errorDetail.includes('Please upgrade your role to access GPU-based compute')) {
          // Set credits to 0 and expiration to NA for forbidden access
          this.creditsRemaining = 0;
          this.creditsExpirationDate = 'NA';
          this.creditsExpired = false;
          console.log('Access forbidden - setting credits to 0 and expiration to NA');
          return;
        }
      }
      
      // Keep default values on other errors
    }
  }

  /**
   * Check if user already has a pending credit request (GET user/credit/request).
   * Sets hasPendingCreditRequest so the UI can show a message or disable duplicate requests.
   */
  async checkPendingCreditRequest(): Promise<void> {
    this.pendingCreditRequestLoading = true;
    this.pendingCreditRequestData = null;
    this.pendingCreditRequestId = null;
    try {
      const response = await this.authApiService.getUserCreditRequest(1, 10);
      const list: any[] = response?.result ?? response?.data ?? response?.items ?? (Array.isArray(response) ? response : []);
      const pendingItem = list.find(
        (item: any) => String(item?.status ?? '').toLowerCase() === 'pending'
      );
      this.hasPendingCreditRequest = !!pendingItem;
      if (pendingItem) {
        this.pendingCreditRequestData = pendingItem.additionalInfo ?? pendingItem.additional_info ?? pendingItem;
        this.pendingCreditRequestId = pendingItem.id ?? pendingItem.requestId ?? null;
      }
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error checking pending credit request:', error);
      this.hasPendingCreditRequest = false;
      this.pendingCreditRequestData = null;
      this.pendingCreditRequestId = null;
    } finally {
      this.pendingCreditRequestLoading = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  openWithdrawCreditModal(): void {
    this.showWithdrawCreditModal = true;
  }

  closeWithdrawCreditModal(): void {
    this.showWithdrawCreditModal = false;
    this.withdrawCreditLoading = false;
  }

  openRequestCreditsHistoryModal(): void {
    this.showRequestCreditsHistoryModal = true;
    this.loadCreditRequestHistory();
  }

  closeRequestCreditsHistoryModal(): void {
    this.showRequestCreditsHistoryModal = false;
    this.openHistoryActionIndex = null;
  }

  toggleHistoryActionDropdown(event: Event, index: number): void {
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    
    this.openHistoryActionIndex = this.openHistoryActionIndex === index ? null : index;
    
    // Trigger change detection immediately
    this.cdr.detectChanges();
    
    // Set dropdown position using fixed positioning
    if (this.openHistoryActionIndex === index) {
      setTimeout(() => {
        const dropdown = document.querySelector('.action-dropdown') as HTMLElement;
        if (dropdown) {
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
        }
      }, 0);
    }
  }

  fillFromHistory(row: { requestedOn: string; status: string; id?: string; originalData?: any }): void {
    // Close the dropdown and modal
    this.openHistoryActionIndex = null;
    this.showRequestCreditsHistoryModal = false;

    // Get additionalInfo from originalData
    const additionalInfo = row.originalData?.additionalInfo ?? row.originalData?.additional_info ?? row.originalData;
    
    // Pass to request-credits component to auto-fill the form
    if (this.requestCreditsComponent) {
      this.requestCreditsComponent.fillFormFromHistory(additionalInfo);
    }
  }

  async loadCreditRequestHistory(): Promise<void> {
    this.creditRequestHistoryLoading = true;
    this.creditRequestHistory = [];
    try {
      const response = await this.authApiService.getUserCreditRequest(1, 100, 'requestedAt:desc');
      const list: any[] = response?.result ?? response?.data ?? response?.items ?? (Array.isArray(response) ? response : []);
      this.creditRequestHistory = list.map((item: any) => {
        const requestedAt = item.requestedAt ?? item.requested_at ?? '';
        const status = (item.status ?? 'N/A').trim();
        const statusFormatted = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'N/A';
        return {
          requestedOn: requestedAt ? this.formatDate(requestedAt) : 'N/A',
          status: statusFormatted,
          id: item.id ?? item.requestId,
          originalData: item,
        };
      });
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading credit request history:', error);
      this.creditRequestHistory = [];
    } finally {
      this.creditRequestHistoryLoading = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  getStatusChipClass(status: string): string {
    if (!status) return 'status-chip status-chip-default';
    const s = status.toLowerCase();
    if (s === 'pending' || s === 'pending approval') return 'status-chip status-chip-pending';
    if (s === 'granted' || s === 'approved') return 'status-chip status-chip-granted';
    if (s === 'rejected') return 'status-chip status-chip-rejected';
    return 'status-chip status-chip-default';
  }

  /**
   * Called when the user successfully submits the Request Credits form.
   * Refreshes pending state so the pending warning div with withdraw button is shown.
   */
  async onCreditRequestSubmitted(): Promise<void> {
    this.googleAnalytics.trackMyProjectRequestCredits();
    await this.checkPendingCreditRequest();
  }

  async confirmWithdrawCreditRequest(): Promise<void> {
    const id = this.pendingCreditRequestId;
    if (!id) {
      this.toasterService.error('Request ID is missing. Cannot withdraw.');
      this.closeWithdrawCreditModal();
      return;
    }
    this.withdrawCreditLoading = true;
    try {
      await this.authApiService.withdrawUserCreditRequest(id);
      this.toasterService.success('Credit request has been withdrawn successfully.');
      this.closeWithdrawCreditModal();
      await this.checkPendingCreditRequest();
    } catch (error) {
      console.error('Error withdrawing credit request:', error);
      this.toasterService.error('Failed to withdraw credit request. Please try again.');
    } finally {
      this.withdrawCreditLoading = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  /**
   * Check if credits are expired based on expiration date
   */
  isCreditsExpired(): boolean {
    if (!this.creditsExpirationDate) {
      return false;
    }
    const expiryDate = new Date(this.creditsExpirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    return expiryDate < today;
  }

  onModalCancel(): void {
    this.showCreateNotebookModal = false;
    this.notebookForm.reset();
    this.initNotebookForm();
  }

  onModalClose(): void {
    this.showCreateNotebookModal = false;
    this.notebookForm.reset();
    this.initNotebookForm();
  }

  selectInstanceType(value: string): void {
    // Prevent GPU selection until user has Compute in active roles array (not just pending)
    if (value === 'GPU' && !this.hasActiveComputeRole) {
      return;
    }
    
    this.notebookForm.patchValue({ instanceType: value });
    this.notebookForm.get('instanceType')?.markAsTouched();
    
    // GPU no longer requires selecting a specific "userCategory".
    // Clear any previously selected GPU instance type.
    if (value === 'CPU' || value === 'GPU') {
      this.notebookForm.patchValue({ userCategory: '' });
      this.userCategoryOptions = [];
      this.gpuInstanceTypes = [];
    }
  }

  selectUserCategory(value: string): void {
    this.notebookForm.patchValue({ userCategory: value });
    this.notebookForm.get('userCategory')?.markAsTouched();
  }

  async onSubmitNotebook(): Promise<void> {
    if (this.notebookForm.valid) {
      const formData = this.notebookForm.value;
      console.log('Creating notebook:', formData);
      
      try {
        this.loading = true;
        
        // Prepare payload according to API requirements
        const notebookType = formData.instanceType?.toLowerCase() || 'cpu';
        const payload: Record<string, unknown> = {
          name: formData.name,
          type: notebookType,
        };
        // GPU: user category is hidden, so we don't pass a specific instanceType.
        
        console.log('Creating notebook with payload:', payload);
        const baseUrl = this.getSandboxBaseUrlForNotebook(formData.instanceType);
        const response = await this.sandboxApiService.createNotebook(payload, baseUrl);
        console.log('Notebook created successfully:', response);
        this.googleAnalytics.trackMyProjectNotebookCreate(formData.name);

        // Show success toaster message
        this.toasterService.success("You've successfully created a Notebook");
        
        // Reset form and switch to list tab
        this.notebookForm.reset();
        this.initNotebookForm();
        this.activeTab = 'list';
        
        // Refresh the notebook list after successful creation
        this.notebooksLoaded = false; // Reset flag to allow reload
        await this.loadNotebooks();
      } catch (error: any) {
        console.error('Error creating notebook:', error);
        
        // Check if error is 400 Bad Request
        if (error?.status === 400 || error?.response?.status === 400 || error?.error?.status === 400) {
          this.toasterService.error('Please try again later or contact administrator.');
        } else {
          // For other errors, show a generic error message
          this.toasterService.error('Failed to create notebook. Please try again.');
        }
        // Keep form open so user can retry
      } finally {
        this.loading = false;
      }
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.notebookForm.controls).forEach(key => {
        this.notebookForm.get(key)?.markAsTouched();
      });
    }
  }

  openPolicyModal(): void {
    this.showPolicyModal = true;
  }

  closePolicyModal(): void {
    this.showPolicyModal = false;
  }

  /**
   * Open notebook - get URL and show confirmation modal
   */
  async openNotebook(row: ProjectData): Promise<void> {
    try {
      const notebookName = row.notebookName || row.id;
      if (!notebookName) {
        this.toasterService.error('Notebook name is missing. Cannot open notebook.');
        return;
      }

      // Get notebook status/URL from API (pass base URL by notebook type for CPU/GPU)
      const baseUrl = this.getSandboxBaseUrlForNotebook(row.type);
      const response = await this.sandboxApiService.notebookStatusCheck(notebookName, baseUrl);
      console.log('Notebook status check response for opening:', response);
      
      // Extract notebook URL from response
      let notebookUrl: string | null = null;
      if (response?.url) {
        notebookUrl = response.url;
      } else if (response?.data?.url) {
        notebookUrl = response.data.url;
      } else if (response?.result?.url) {
        notebookUrl = response.result.url;
      } else if (response?.notebookUrl) {
        notebookUrl = response.notebookUrl;
      } else if (response?.data?.notebookUrl) {
        notebookUrl = response.data.notebookUrl;
      } else if (response?.result?.notebookUrl) {
        notebookUrl = response.result.notebookUrl;
      }
      
      // Show the confirmation modal with the notebook URL
      this.showNotebookReadyConfirmation(row, notebookUrl);
    } catch (error) {
      console.error('Error getting notebook URL:', error);
      // Still show modal with constructed URL as fallback
      this.showNotebookReadyConfirmation(row, null);
    }
  }

  /**
   * Get sandbox base URL for a notebook by type (CPU vs GPU).
   * Uses environment.baseSandboxPathUrlCpu / baseSandboxPathUrlGpu so production can use different domains.
   * For GPU we prefer baseSandboxPathUrlGpu; if missing (e.g. wrong build config), fall back to CPU base so the request never goes to the app origin as a relative URL.
   */
  getSandboxBaseUrlForNotebook(type?: string): string {
    const t = (type || '').trim().toUpperCase();
    const env = environment as { baseSandboxPathUrlCpu?: string; baseSandboxPathUrlGpu?: string };
    const cpuBase = env.baseSandboxPathUrlCpu ?? '';
    const gpuBase = env.baseSandboxPathUrlGpu ?? '';
    if (t === 'GPU') {
      const url = gpuBase || cpuBase;
      if (!gpuBase && cpuBase) {
        console.warn('MahaAgX: baseSandboxPathUrlGpu is not set; using CPU sandbox base for GPU notebook. Build with --configuration=production to use production env.');
      }
      return url;
    }
    if (t === 'CPU') return cpuBase;
    return cpuBase;
  }

  /**
   * Show confirmation modal when notebook status changes to "Running"
   */
  showNotebookReadyConfirmation(notebook: ProjectData, notebookUrl?: string | null): void {
    this.readyNotebook = notebook;
    
    // Use URL from API response if available, otherwise construct it
    if (notebookUrl) {
      this.readyNotebookUrl = notebookUrl;
    } else {
      // Construct notebook URL from notebook name; use CPU/GPU base URL by type
      const notebookName = notebook.notebookName || notebook.id;
      const baseUrl = this.getSandboxBaseUrlForNotebook(notebook.type);
      this.readyNotebookUrl = `${baseUrl}notebook/${notebookName}`;
    }
    
    this.showNotebookReadyModal = true;
  }

  /**
   * Handle proceed button click - open notebook in new tab
   */
  onProceedToNotebook(): void {
    if (this.readyNotebookUrl) {
      // Open notebook in a new page/tab
      window.open(this.readyNotebookUrl, '_blank');
    }
    // Close modal after opening
    this.closeNotebookReadyModal();
  }

  /**
   * Check if user has Compute role
   */
  private async checkComputeRole(): Promise<void> {
    try {
      console.log('Checking Compute role for GPU access...');
      const userData = await this.authApiService.getUserInfo();
      console.log('User API response for role check:', userData);
      
      // Extract result if nested, otherwise use response directly
      const actualUserData = userData?.['result'] || userData;
      const userRoles = actualUserData?.roles || [];
      
      // Extract pendingRoles - check multiple possible locations
      this.pendingRoles = actualUserData?.['pendingRoles'] || 
                         userData?.['pendingRoles'] || 
                         [];
      
      console.log('Full user data:', actualUserData);
      console.log('Pending roles:', this.pendingRoles);
      console.log('Has pending compute role:', this.hasPendingComputeRole());
      
      // Check if user has compute role (in active roles array)
      const hasActiveComputeRole = userRoles.some((role: string) => role.toLowerCase() === 'compute');
      this.hasActiveComputeRole = hasActiveComputeRole;
      
      // Check if user has compute role in pending roles
      const hasPendingCompute = this.hasPendingComputeRole();
      
      // User has compute role if it's either active or pending (for enabling GPU / Request Credits)
      this.hasComputeRole = hasActiveComputeRole || hasPendingCompute;
      
      console.log('User roles:', userRoles, 'Has active Compute role:', hasActiveComputeRole, 'Has pending Compute role:', hasPendingCompute, 'Has Compute role (any):', this.hasComputeRole);
      
      // Trigger change detection to update the warning message
      this.cdr.detectChanges();
      
      // If user doesn't have Compute in active roles and GPU is selected, switch to CPU
      if (!this.hasActiveComputeRole && this.notebookForm.get('instanceType')?.value === 'GPU') {
        this.notebookForm.patchValue({ instanceType: 'CPU' });
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Error checking Compute role:', error);
      // On error, assume no compute role for safety
      this.hasComputeRole = false;
      this.hasActiveComputeRole = false;
      this.pendingRoles = [];
    }
  }
  
  /**
   * Check if GPU option is disabled (disabled until Compute is in active roles array, not just pending)
   */
  isGpuDisabled(): boolean {
    return !this.hasActiveComputeRole;
  }
  
  /**
   * Check if compute role is pending approval
   */
  hasPendingComputeRole(): boolean {
    return this.pendingRoles.some((role: string) => role.toLowerCase() === 'compute');
  }
  

  /**
   * Handle stop button click - stop the notebook
   */
  async onStopNotebookFromModal(): Promise<void> {
    console.log('onStopNotebookFromModal called');
    console.log('readyNotebook:', this.readyNotebook);
    
    if (!this.readyNotebook) {
      console.warn('No ready notebook found to stop');
      return;
    }

    const notebookName = this.readyNotebook.notebookName;
    console.log('Notebook name to stop:', notebookName);
    
    if (!notebookName) {
      console.warn('Notebook name is missing');
      this.toasterService.error('Notebook name is missing. Cannot stop notebook.');
      this.closeNotebookReadyModal();
      return;
    }

    // Stop the notebook without showing loading skeleton
    try {
      const payload = {
        name: notebookName
      };
      
      console.log('=== STOPPING NOTEBOOK FROM MODAL ===');
      console.log('Payload:', payload);
      console.log('sandboxApiService:', this.sandboxApiService);
      console.log('Calling sandboxApiService.stopNotebook...');
      const baseUrl = this.getSandboxBaseUrlForNotebook(this.readyNotebook?.type);
      const response = await this.sandboxApiService.stopNotebook(payload, baseUrl);
      console.log('=== NOTEBOOK STOPPED SUCCESSFULLY ===');
      console.log('Response:', response);
      
      // Close modal after successful API call
      this.closeNotebookReadyModal();
      
      // Update the status locally immediately for better UX
      const notebookIndex = this.projectsData.findIndex(
        (n) => n.notebookName === notebookName
      );
      if (notebookIndex !== -1) {
        this.projectsData[notebookIndex].status = 'Stopped';
        this.cdr.detectChanges();
      }
      
      // Refresh the notebook list after a delay without showing skeleton
      setTimeout(async () => {
        this.notebooksLoaded = false;
        await this.loadNotebooks(true); // Pass true to skip loading state
      }, 1000);
    } catch (error) {
      console.error('Error stopping notebook from modal:', error);
      this.toasterService.error('Failed to stop notebook. Please try again.');
      // Close modal even on error
      this.closeNotebookReadyModal();
      // Refresh list on error - but try to avoid skeleton
      setTimeout(async () => {
        this.notebooksLoaded = false;
        await this.loadNotebooks(true); // Pass true to skip loading state
      }, 500);
    }
  }

  /**
   * Close the notebook ready modal
   */
  closeNotebookReadyModal(): void {
    this.showNotebookReadyModal = false;
    this.readyNotebook = null;
    this.readyNotebookUrl = null;
  }

  /**
   * Check if there are any notebooks with "Opening" status
   */
  hasOpeningNotebooks(): boolean {
    return this.projectsData.some(notebook => this.isOpeningStatus(notebook.status));
  }

  /**
   * Get all notebooks with "Opening" status
   */
  getOpeningNotebooks(): ProjectData[] {
    return this.projectsData.filter(notebook => this.isOpeningStatus(notebook.status));
  }

  /**
   * HostListener for browser navigation (beforeunload)
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasOpeningNotebooks()) {
      event.preventDefault();
      event.returnValue = 'You have notebooks that are opening. Please wait or delete them before leaving.';
      return event.returnValue;
    }
  }

  /**
   * Intercept navigation attempts
   * This should be called before any navigation
   */
  async checkNavigationBeforeLeave(url?: string): Promise<boolean> {
    if (this.hasOpeningNotebooks()) {
      this.openingNotebooks = this.getOpeningNotebooks();
      this.pendingNavigation = url || null;
      this.showNavigationGuardModal = true;
      this.cdr.detectChanges();
      return false; // Prevent navigation
    }
    return true; // Allow navigation
  }

  /**
   * Handle user choice: Wait for notebooks to open
   */
  async handleWaitForNotebooks(): Promise<void> {
    this.showNavigationGuardModal = false;
    this.pendingNavigation = null;
    this.openingNotebooks = [];
    this.cdr.detectChanges();
    // User stays on the page, notebooks continue opening
  }

  /**
   * Handle user choice: Delete opening notebooks
   */
  async handleDeleteOpeningNotebooks(): Promise<void> {
    try {
      const notebooksToDelete = [...this.openingNotebooks];
      const pendingNav = this.pendingNavigation;
      
      // Delete all opening notebooks
      for (const notebook of notebooksToDelete) {
        try {
          await this.deleteNotebook(notebook);
        } catch (error) {
          console.error(`Error deleting notebook ${notebook.notebookName}:`, error);
        }
      }
      
      this.showNavigationGuardModal = false;
      this.openingNotebooks = [];
      
      // If there was a pending navigation, proceed with it
      if (pendingNav) {
        this.pendingNavigation = null;
        // Wait a bit for the deletion to complete and UI to update
        setTimeout(() => {
          if (pendingNav.startsWith('/')) {
            this.router.navigateByUrl(pendingNav);
          } else {
            // Handle relative paths
            this.router.navigate([pendingNav]);
          }
        }, 300);
      }
      
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error deleting opening notebooks:', error);
      this.toasterService.error('Failed to delete some notebooks. Please try again.');
    }
  }

  /**
   * Close navigation guard modal without action
   */
  closeNavigationGuardModal(): void {
    this.showNavigationGuardModal = false;
    this.pendingNavigation = null;
    this.openingNotebooks = [];
    this.cdr.detectChanges();
  }

  /**
   * Open upgrade role modal by dispatching custom event
   */
  openUpgradeRoleModal(): void {
    const event = new CustomEvent('openUpgradeModal', {
      detail: { source: 'my-projects' }
    });
    window.dispatchEvent(event);
  }

  /**
   * CanDeactivate implementation for route guard
   */
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (!this.hasOpeningNotebooks()) {
      return true;
    }

    // Show the modal and return a promise that resolves based on user action
    return new Promise<boolean>((resolve) => {
      this.openingNotebooks = this.getOpeningNotebooks();
      this.showNavigationGuardModal = true;
      this.cdr.detectChanges();

      // Store the resolve function so we can call it when user makes a choice
      (this as any)._navigationResolve = resolve;
    });
  }

  /**
   * Handle user choice: Wait for notebooks to open (cancel navigation)
   */
  async handleWaitForNotebooksNavigation(): Promise<void> {
    const resolve = (this as any)._navigationResolve;
    if (resolve) {
      (this as any)._navigationResolve = null;
      resolve(false); // Cancel navigation
    }
    await this.handleWaitForNotebooks();
  }

  /**
   * Handle user choice: Delete opening notebooks (allow navigation)
   */
  async handleDeleteOpeningNotebooksNavigation(): Promise<void> {
    const resolve = (this as any)._navigationResolve;
    if (resolve) {
      (this as any)._navigationResolve = null;
    }
    
    try {
      const notebooksToDelete = [...this.openingNotebooks];
      
      // Delete all opening notebooks
      for (const notebook of notebooksToDelete) {
        try {
          await this.deleteNotebook(notebook);
        } catch (error) {
          console.error(`Error deleting notebook ${notebook.notebookName}:`, error);
        }
      }
      
      this.showNavigationGuardModal = false;
      this.openingNotebooks = [];
      this.cdr.detectChanges();
      
      // Resolve to allow navigation
      if (resolve) {
        resolve(true);
      }
    } catch (error) {
      console.error('Error deleting opening notebooks:', error);
      this.toasterService.error('Failed to delete some notebooks. Please try again.');
      // Still allow navigation if user wants to proceed
      if (resolve) {
        resolve(true);
      }
    }
  }
}
